import { useEffect, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { Button, Input, cx } from "./ui";
import {
  emptyRow,
  envProblems,
  looksSecret,
  mergeRows,
  rowsFromText,
  type EnvRow,
} from "../lib/envRows";

/*
 * One grid template for the header and every row, so the columns line up by
 * construction. Below `sm` the key takes the whole first line and the value
 * shares the second with the two buttons, which keeps a phone from stacking
 * four controls into an unlabelled pile.
 */
const ROW_COLS =
  "grid-cols-[minmax(0,1fr)_auto_auto] gap-x-2 gap-y-1.5 " +
  "sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto_auto]";

/**
 * The environment a workload is created with, as a list of rows.
 *
 * Built for how developers actually arrive at this step: with a `.env` file
 * open in another window. Pasting it into any key field fills the list — the
 * paste is parsed, not inserted — and "Paste .env" opens a box for the same
 * thing when the clipboard is not to hand. Typed one at a time, Enter in a
 * value moves to the next row and adds one at the end, so a whole file can be
 * entered without touching the mouse.
 *
 * A name that looks like a credential locks its row on its own, and a URL
 * carrying a password does the same; the lock is a button, so the guess costs
 * one click to overrule and never argues back once it has been touched.
 * Problems are said under the row they are about, in the same words the
 * Continue button uses when it refuses.
 */
export function EnvEditor({
  rows,
  onChange,
  idPrefix = "env",
}: {
  rows: EnvRow[];
  onChange: (rows: EnvRow[]) => void;
  /** Namespaces the inputs' ids, so two editors on one page cannot collide. */
  idPrefix?: string;
}) {
  const { t } = useTranslation("create");
  const [pasting, setPasting] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<number | null>(null);
  const problems = new Map(envProblems(rows).map((p) => [p.id, p.message]));

  const keyInputId = (id: number) => `${idPrefix}-${id}-key`;

  // Focus follows the row that was just added or moved to, once it exists.
  useEffect(() => {
    if (focusId === null) return;
    document.getElementById(keyInputId(focusId))?.focus();
    setFocusId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, rows]);

  function update(id: number, patch: Partial<EnvRow>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  /** The secret guess runs while the name is typed and stops once the lock is touched. */
  function guessed(row: EnvRow, key: string, value: string): boolean {
    return row.secretTouched ? row.isSecret : looksSecret(key, value);
  }

  function add() {
    const row = emptyRow();
    onChange([...rows, row]);
    setFocusId(row.id);
  }

  function remove(id: number) {
    const left = rows.filter((row) => row.id !== id);
    onChange(left.length > 0 ? left : [emptyRow()]);
  }

  /** A `.env` pasted into a key field is a list, not a name. */
  function pasteIntoKey(index: number, event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData("text");
    if (!text.includes("=")) return;
    const parsed = rowsFromText(text);
    if (parsed.rows.length === 0) return;
    event.preventDefault();
    onChange(mergeRows(rows, parsed.rows, index));
  }

  function enterInValue(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter") return;
    // The form would submit; on a wizard step that is Continue, not intended.
    event.preventDefault();
    const next = rows[index + 1];
    if (next) setFocusId(next.id);
    else add();
  }

  function applyPaste() {
    const parsed = rowsFromText(pasteText);
    if (parsed.invalidLines.length > 0) {
      setPasteError(t("env.pasteInvalid", { lines: parsed.invalidLines.join(", ") }));
      return;
    }
    onChange(mergeRows(rows, parsed.rows));
    closePaste();
  }

  function closePaste() {
    setPasting(false);
    setPasteText("");
    setPasteError(null);
  }

  return (
    <div className="space-y-2">
      <div className={cx("hidden sm:grid", ROW_COLS)}>
        <span className="micro">{t("env.key")}</span>
        <span className="micro">{t("env.value")}</span>
        <span className="sr-only">{t("env.secret")}</span>
        <span className="sr-only">{t("env.remove")}</span>
      </div>

      {rows.map((row, index) => {
        const problem = problems.get(row.id);
        return (
          <div key={row.id} className="space-y-1">
            <div className={cx("grid sm:items-center", ROW_COLS)}>
              <Input
                id={keyInputId(row.id)}
                aria-label={t("env.key")}
                aria-invalid={problem ? true : undefined}
                value={row.key}
                placeholder="KEY"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) =>
                  update(row.id, {
                    key: e.target.value,
                    isSecret: guessed(row, e.target.value, row.value),
                  })
                }
                onPaste={(e) => pasteIntoKey(index, e)}
                className="col-span-3 font-mono sm:col-span-1"
              />
              <Input
                aria-label={t("env.value")}
                type={row.isSecret ? "password" : "text"}
                value={row.value}
                placeholder="value"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) =>
                  update(row.id, {
                    value: e.target.value,
                    isSecret: guessed(row, row.key, e.target.value),
                  })
                }
                onKeyDown={(e) => enterInValue(index, e)}
                className="font-mono"
              />
              <button
                type="button"
                aria-pressed={row.isSecret}
                aria-label={row.isSecret ? t("env.secret") : t("env.plain")}
                title={row.isSecret ? t("env.secretOn")
                : t("env.secretOff")}
                onClick={() => update(row.id, { isSecret: !row.isSecret, secretTouched: true })}
                className={cx(
                  "grid size-9 shrink-0 place-items-center rounded-md border transition-colors duration-200 ease-smooth",
                  row.isSecret
                    ? "border-accent/40 bg-accent-soft text-accent"
                    : "border-line text-fg-subtle hover:border-line-strong hover:text-fg",
                )}
              >
                <Icon name="lock" className="size-3.5" />
              </button>
              <button
                type="button"
                aria-label={t("env.remove")}
                title={t("env.remove")}
                onClick={() => remove(row.id)}
                className="grid size-9 shrink-0 place-items-center rounded-md text-fg-subtle transition-colors duration-200 ease-smooth hover:bg-raised hover:text-fg"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </div>
            {problem && (
              <p className="flex items-center gap-1.5 text-[13px] text-danger">
                <Icon name="warning" className="size-3.5 shrink-0" />
                {problem}
              </p>
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="button" variant="secondary" size="sm" onClick={add}>
          <Icon name="plus" className="size-3.5" />
          {t("env.add")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={pasting}
          onClick={() => (pasting ? closePaste() : setPasting(true))}
        >
          {t("env.paste")}
        </Button>
      </div>

      {pasting && (
        <div className="space-y-2 rounded-lg border border-line bg-sunken p-3">
          <textarea
            aria-label={t("env.pasteLabel")}
            rows={6}
            autoFocus
            spellCheck={false}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={t("env.pastePlaceholder")}
            className="w-full rounded-md border border-line bg-canvas px-3 py-2 font-mono text-sm pointer-coarse:text-base text-fg placeholder:text-fg-subtle"
          />
          {pasteError && (
            <p className="flex items-center gap-1.5 text-[13px] text-danger">
              <Icon name="warning" className="size-3.5 shrink-0" />
              {pasteError}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={applyPaste} disabled={pasteText.trim() === ""}>
              {t("env.addThese")}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={closePaste}>
              {t("env.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
